import { AccessLevel, PLAN_LIMITS, SubscriptionPlanKey } from "@authory-packages/core";

import type { JSX } from "react";

const SvgCheckIcon = () => <img src="/authory_static_files/images/icons/check_mark.svg" />;

const FREE_LIMITS = PLAN_LIMITS[AccessLevel.Free];
const LITE_LIMITS = PLAN_LIMITS[AccessLevel.Lite];

export type PlanDownside = { label: string };

/**
 * Copy shown on a plan card inside the cancellation flow only.
 */
export type PlanCancellationCopy = {
    tagline: string;
    changes: string[];
    footnote?: string;
};

type PlanTier = {
    price: number;
    yearlyDiscountFactor: number;
    label: string;
    tagline: string;
    hasItemCap?: boolean;
    freeDomainBanner?: boolean;
    downgradeRecap?: string[];
    scheduledDowngradeRecap?: string[];
    scheduledDowngradeWarning?: string;
    downgradeNote?: string;
    cancellationCopy?: PlanCancellationCopy;
    perks: { label: string; icon: JSX.Element; tooltip: string }[];
    downsides?: PlanDownside[];
};

/**
 * A cancellation bullet may end with a parenthetical aside ("(you'll choose which to keep)") that
 * only makes sense for a user who is above the target plan's item limit. Someone already within it
 * keeps everything and is never asked to choose, so the aside is dropped for them.
 */
export const formatCancellationChange = (change: string, isOverItemLimit: boolean): string =>
    isOverItemLimit ? change : change.replace(/\s*\([^)]*\)\s*$/, "");

export const AUTHORY_2023_PLAN_TIERS = {
    FREE: {
        price: 0,
        yearlyDiscountFactor: 0,
        label: "Free",
        tagline: "Keep your best work safe and shareable at no cost.",
        hasItemCap: true,
        downgradeRecap: [
            `Limited to ${FREE_LIMITS.content_items} content items (you currently have [X] - you'll choose which to keep)`,
            `Limited to ${FREE_LIMITS.smart_share_links} Smart Share ${FREE_LIMITS.smart_share_links === 1 ? "link" : "links"} (if you have more, then the older ones will be removed)`,
            "Auto-updating will be turned off",
            "Audio and video uploads will be removed",
            "Your custom domain will be removed",
            "Search engine indexing disabled",
            "Authory branding will appear on your portfolio"
        ],
        scheduledDowngradeRecap: [
            "Auto-updating will be turned off",
            "Your portfolio will remain online",
            `If you're above Free's ${FREE_LIMITS.content_items}-item limit, we'll email you and guide you through choosing which items to keep.`
        ],
        scheduledDowngradeWarning:
            "Nothing will be deleted or hidden until you make your selection. After you choose your items, anything beyond Free's limits will be permanently deleted from your account.",
        downgradeNote: "Important: Deleted items cannot be recovered, even if you switch back to a higher plan.",
        cancellationCopy: {
            tagline: "Keep your account active at no cost, but with a number of limitations.",
            changes: [
                `Limited to ${FREE_LIMITS.content_items} content items (you'll choose which to keep)`,
                `Limited to ${FREE_LIMITS.smart_share_links} Smart Share ${FREE_LIMITS.smart_share_links === 1 ? "link" : "links"}`,
                "Auto-updating will be turned off",
                "Audio and video uploads removed",
                "Search engine indexing disabled",
                "Authory branding on your portfolio",
                "Your custom domain removed"
            ]
        },
        perks: [
            {
                label: "Manually updated portfolio page",
                icon: <SvgCheckIcon />,
                tooltip: "Create and share your own portfolio page with your articles, podcasts, and videos."
            },
            {
                label: "Up to 10 content items",
                icon: <SvgCheckIcon />,
                tooltip: "Add up to 10 content items to your searchable archive."
            },
            {
                label: "1 Smart Share link",
                icon: <SvgCheckIcon />,
                tooltip: "Create 1 Smart Share link to share a curated selection of your content."
            },
            {
                label: "Searchable archive with full backups",
                icon: <SvgCheckIcon />,
                tooltip:
                    "Your personal content archive makes your entire body of work 100% safe against deletion, plus it's easy to search and filter."
            }
        ],
        downsides: [{ label: "Authory branding on your portfolio" }, { label: "No search engine indexing" }]
    },
    LITE: {
        price: 12,
        yearlyDiscountFactor: 9 / 12,
        label: "Lite",
        tagline: "A portfolio for early-stage creators who want their work in one place.",
        hasItemCap: true,
        downgradeRecap: [
            `Limited to ${LITE_LIMITS.content_items} content items (you currently have [X] - you'll choose which to keep)`,
            `Limited to ${LITE_LIMITS.smart_share_links} Smart Share ${LITE_LIMITS.smart_share_links === 1 ? "link" : "links"} (if you have more, then the older ones will be removed)`,
            "Auto-updating will be turned off",
            "Audio and video uploads will be removed",
            "Your custom domain will be removed"
        ],
        scheduledDowngradeRecap: [
            "Auto-updating will be turned off",
            "Your portfolio will remain online",
            `If you're above Lite's ${LITE_LIMITS.content_items}-item limit, we'll email you and guide you through choosing which items to keep.`
        ],
        scheduledDowngradeWarning:
            "Nothing will be deleted or hidden until you make your selection. After you choose your items, anything beyond Lite's limits will be permanently deleted from your account.",
        downgradeNote: "Important: Deleted items cannot be recovered, even if you switch back to a higher plan.",
        cancellationCopy: {
            tagline: `Reduce your archive and keep up to ${LITE_LIMITS.content_items} items.`,
            changes: [
                `Limited to ${LITE_LIMITS.content_items} content items (you'll choose which to keep)`,
                `Limited to ${LITE_LIMITS.smart_share_links} Smart Share ${LITE_LIMITS.smart_share_links === 1 ? "link" : "links"}`,
                "Auto-updating will be turned off",
                "Audio and video uploads removed",
                "Your custom domain will be removed"
            ]
        },
        perks: [
            {
                label: "Manually updated portfolio page",
                icon: <SvgCheckIcon />,
                tooltip: "Create and share your own portfolio page with your articles, podcasts, and videos."
            },
            {
                label: "Up to 50 content items",
                icon: <SvgCheckIcon />,
                tooltip: "Add up to 50 content items to your searchable archive."
            },
            {
                label: "5 Smart Share links",
                icon: <SvgCheckIcon />,
                tooltip: "Create up to 5 Smart Share links to share curated selections of your content."
            },
            {
                label: "Searchable archive with full backups",
                icon: <SvgCheckIcon />,
                tooltip:
                    "Your personal content archive makes your entire body of work 100% safe against deletion, plus it's easy to search and filter."
            },
            {
                label: "Social media analytics",
                icon: <SvgCheckIcon />,
                tooltip: "Authory will tell you exactly how often each piece of content you create is shared on social media."
            }
        ]
    },
    STANDARD: {
        price: 19,
        yearlyDiscountFactor: 15 / 19,
        label: "Standard",
        tagline: "A fully automated, always-updated portfolio with unlimited everything.",
        freeDomainBanner: true,
        downgradeRecap: [
            "API access will no longer be available",
            "Google Analytics and Search Console integrations will be disconnected",
            "Audio and video uploads will be limited"
        ],
        downgradeNote: "Your portfolio, all content items, custom domain, and Smart Share links stay exactly as they are.",
        scheduledDowngradeWarning: "Your portfolio will continue running without interruption.",
        cancellationCopy: {
            tagline: "Your full portfolio, all content items, and custom domain stay as they are.",
            changes: [
                "API access will no longer be available",
                "Google Analytics and Search Console integrations will be disconnected",
                "Audio and video uploads will be limited"
            ],
            footnote: "Everything else stays as is."
        },
        perks: [
            {
                label: "Self-updating portfolio (automatic import of past & future work)",
                icon: <SvgCheckIcon />,
                tooltip:
                    "Authory automatically finds, imports and updates all your existing and future articles, podcasts and videos, no matter where they are published."
            },
            {
                label: "Unlimited content items",
                icon: <SvgCheckIcon />,
                tooltip: "Add an unlimited number of articles, videos, podcasts, PDFs, screenshots or social media posts to your account."
            },
            {
                label: "Unlimited Smart Share links",
                icon: <SvgCheckIcon />,
                tooltip: "Create unlimited Smart Share links to share curated selections of your content."
            },
            {
                label: "Searchable archive with full backups",
                icon: <SvgCheckIcon />,
                tooltip:
                    "Your personal content archive makes your entire body of work 100% safe against deletion, plus it's easy to search and filter."
            },
            {
                label: "Custom domain support",
                icon: <SvgCheckIcon />,
                tooltip: "Host your self-updating portfolio page at a domain that you own."
            },
            {
                label: "Audio and video uploads",
                icon: <SvgCheckIcon />,
                tooltip: "Upload audio and video files directly to your Authory archive."
            }
        ]
    },
    PRO: {
        price: 29,
        yearlyDiscountFactor: 24 / 29,
        label: "Professional",
        tagline: "For power users who need integrations and large media support.",
        freeDomainBanner: true,
        downgradeRecap: [],
        perks: [
            {
                label: "API access",
                icon: <SvgCheckIcon />,
                tooltip: "Connect directly to our platform with our custom built API."
            },
            {
                label: "Zapier integration",
                icon: <SvgCheckIcon />,
                tooltip: "Our Zapier integration enables you to connect Authory easily to other apps."
            },
            {
                label: "Google Analytics & Search Console integration",
                icon: <SvgCheckIcon />,
                tooltip: "Connect your Google Analytics and Search Console accounts to track your portfolio's performance."
            },
            {
                label: "Support for large audio & video files",
                icon: <SvgCheckIcon />,
                tooltip: "Upload and manage large audio and video files without restrictions."
            }
        ]
    }
} satisfies Record<string, PlanTier>;

/**
 * Widens a tier's cancellation copy out of its literal type, so callers can read the optional
 * `footnote` on tiers that define one without hand-narrowing the union.
 */
export const getCancellationCopy = (
    tier: (typeof AUTHORY_2023_PLAN_TIERS)[keyof typeof AUTHORY_2023_PLAN_TIERS]
): PlanCancellationCopy | undefined => ("cancellationCopy" in tier ? tier.cancellationCopy : undefined);

export const getPlanFromTier = (tier: keyof typeof AUTHORY_2023_PLAN_TIERS, isYearly: boolean): SubscriptionPlanKey => {
    switch (tier) {
        case "LITE":
            return isYearly ? SubscriptionPlanKey.USDAuthoryLiteYearly : SubscriptionPlanKey.USDAuthoryLiteMonthly;
        case "STANDARD":
            return isYearly ? SubscriptionPlanKey.USDAuthorySub2023Yearly : SubscriptionPlanKey.USDAuthorySub2023Monthly;
        case "PRO":
            return isYearly ? SubscriptionPlanKey.USDAuthoryProSub2023Yearly : SubscriptionPlanKey.USDAuthoryProSub2023Monthly;
        case "FREE":
            return SubscriptionPlanKey.USDAuthoryFreeSub2023Yearly;
    }
};

/**
 * Maps a plan the user selected on the pricing page to the plan their free trial should
 * run on. Reverse-trial rule: a Lite selection always trials on Standard (same billing
 * period); Standard and Pro pass through unchanged. Falls back to Standard yearly when
 * nothing usable was stored.
 */
export const getTrialPlanKeyFromSelection = (selectedPlanKey: string | undefined): SubscriptionPlanKey => {
    switch (selectedPlanKey) {
        case SubscriptionPlanKey.USDAuthoryLiteMonthly:
        case SubscriptionPlanKey.USDAuthorySub2023Monthly:
            return SubscriptionPlanKey.USDAuthorySub2023Monthly;
        case SubscriptionPlanKey.USDAuthoryLiteYearly:
        case SubscriptionPlanKey.USDAuthorySub2023Yearly:
            return SubscriptionPlanKey.USDAuthorySub2023Yearly;
        case SubscriptionPlanKey.USDAuthoryProSub2023Monthly:
            return SubscriptionPlanKey.USDAuthoryProSub2023Monthly;
        case SubscriptionPlanKey.USDAuthoryProSub2023Yearly:
            return SubscriptionPlanKey.USDAuthoryProSub2023Yearly;
        default:
            return SubscriptionPlanKey.USDAuthorySub2023Yearly;
    }
};

export const getTierFromSub = (planKey: SubscriptionPlanKey | null) => {
    switch (planKey) {
        case null:
        case SubscriptionPlanKey.USDAuthorySub2023Monthly:
        case SubscriptionPlanKey.USDAuthorySub2023Yearly:
            return AUTHORY_2023_PLAN_TIERS.STANDARD;
        case SubscriptionPlanKey.USDAuthoryLiteMonthly:
        case SubscriptionPlanKey.USDAuthoryLiteYearly:
            return AUTHORY_2023_PLAN_TIERS.LITE;
        case SubscriptionPlanKey.USDAuthoryProSub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryProSub2023Yearly:
            return AUTHORY_2023_PLAN_TIERS.PRO;
        case SubscriptionPlanKey.USDAuthoryFreeSub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryFreeSub2023Yearly:
        case SubscriptionPlanKey.USDAuthoryFreeProSub2023Monthly:
        case SubscriptionPlanKey.USDAuthoryFreeProSub2023Yearly:
            return AUTHORY_2023_PLAN_TIERS.FREE;
    }
};
